"use client"

import { FC } from "react"
import { PagerChevron } from "./icons"
import { Pagination as PaginationNav, PageButton, ArrowButton } from "./styles"

interface PaginationProps {
  page: number
  totalPages: number
  prevLabel: string
  nextLabel: string
  onChange: (page: number) => void
}

/** Window of page numbers around the current page (max 5), descending for RTL. */
function pageWindow(page: number, totalPages: number): number[] {
  const max = 5
  if (totalPages <= max) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  let start = Math.max(1, page - Math.floor(max / 2))
  const end = Math.min(totalPages, start + max - 1)
  start = Math.max(1, end - max + 1)
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

const Pagination: FC<PaginationProps> = ({
  page,
  totalPages,
  prevLabel,
  nextLabel,
  onChange,
}) => {
  if (totalPages <= 1) return null

  const pages = pageWindow(page, totalPages)
  const isFirst = page <= 1
  const isLast = page >= totalPages

  return (
    <PaginationNav aria-label="pagination">
      {/* prev = lower page number; rendered first so it sits on the start side */}
      <ArrowButton
        type="button"
        data-dir="prev"
        aria-label={prevLabel}
        disabled={isFirst}
        onClick={() => !isFirst && onChange(page - 1)}
      >
        <PagerChevron />
      </ArrowButton>

      {pages.map((p) => (
        <PageButton
          key={p}
          type="button"
          $active={p === page}
          aria-current={p === page ? "page" : undefined}
          onClick={() => onChange(p)}
        >
          {p}
        </PageButton>
      ))}

      <ArrowButton
        type="button"
        data-dir="next"
        aria-label={nextLabel}
        disabled={isLast}
        onClick={() => !isLast && onChange(page + 1)}
      >
        <PagerChevron />
      </ArrowButton>
    </PaginationNav>
  )
}

export default Pagination
